import { Stack, Switch, Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import { MessageSquare, Phone } from 'lucide-react';
import { ReactNode } from 'react';
import { SelectMenu, SelectMenuOption } from 'src/components/SelectMenu';
import { SettingsSection, SettingsSectionRow } from 'src/components/SettingsSection';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { hasPlatformPartnerAccess, useGroupRules } from 'src/graphql';
import {
  PlatformWorkflowOption,
  usePlatformWorkflowsByTrigger,
} from 'src/graphql/entities/workflow/hooks/use-platform-workflows-by-trigger';
import {
  ORDERED_WORKFLOW_TRIGGER_TYPES,
  WORKFLOW_TRIGGER_LABELS,
  WorkflowTriggerType,
} from 'src/graphql/entities/workflow/workflow.trigger';
import { useWorkflowEmergencyOverride } from 'src/pages/Settings/preferences/hooks/use-workflow-emergency-override';
import { useGlobalStore } from 'src/store';

const MASTER_LABEL = 'Override all triggers';

/**
 * The shared `SelectMenu` marks the chosen option with `sys.primary`, the same teal every
 * other select in the portal uses. The design reference marks this one with `sys.secondary`
 * instead, and that is worth honouring here: mid-incident the question is "which workflow is
 * pinned right now", and the answer has to survive a glance rather than a hunt for a tick.
 * Scoped to this call site through `menuSx` — the shared component's own colours are left
 * alone, since repointing them would recolour every select in the portal.
 *
 * Both hooks are behavioural attributes the component emits for its own logic, not test ids:
 * `data-option-value` keys each row, and `data-check` marks the check inside the chosen one.
 */
function workflowMenuSx(workflowId: string | undefined): SystemStyleObject<Theme> {
  if (!workflowId) return {};

  return {
    [`& [data-option-value="${CSS.escape(workflowId)}"]`]: {
      color: 'custom.OV.sys.secondary',
      fontWeight: 600,
      '& [data-check]': { color: 'custom.OV.sys.secondary' },
    },
  };
}

const TRIGGER_ICONS: Record<WorkflowTriggerType, ReactNode> = {
  [WorkflowTriggerType.InboundPhoneCall]: <Phone />,
  [WorkflowTriggerType.InboundSms]: <MessageSquare />,
};

interface TriggerOverrideRowProps {
  triggerType: WorkflowTriggerType;
  workflowId?: string;
  enabled: boolean;
  /** The master switch is on, so this trigger is overridden whatever its own flag says. */
  coveredByMaster: boolean;
  workflows: PlatformWorkflowOption[];
  workflowsLoading: boolean;
  disabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  onWorkflowChange: (workflowId: string) => void;
}

const TriggerOverrideRow = ({
  triggerType,
  workflowId,
  enabled,
  coveredByMaster,
  workflows,
  workflowsLoading,
  disabled,
  onEnabledChange,
  onWorkflowChange,
}: TriggerOverrideRowProps) => {
  const label = WORKFLOW_TRIGGER_LABELS[triggerType];
  const options = buildOptions(workflows, workflowId);

  return (
    <SettingsSectionRow
      icon={TRIGGER_ICONS[triggerType]}
      title={label}
      secondary={triggerSecondary({
        label,
        coveredByMaster,
        workflowsLoading,
        hasWorkflows: !!workflows.length,
      })}
      mobileControlLayout="below"
      control={
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <SelectMenu
            label={`${label} workflow`}
            placeholder="Select a workflow"
            options={options}
            value={workflowId ?? null}
            onChange={onWorkflowChange}
            disabled={disabled || !workflows.length}
            search
            searchPlaceholder="Search workflows…"
            emptyText="No platform workflow for this trigger"
            menuWidth="trigger"
            menuSx={workflowMenuSx(workflowId)}
          />
          <Switch
            checked={enabled}
            // While the master switch is on it owns every trigger: a switch that could be
            // clicked off here without changing what runs would be lying.
            disabled={disabled || coveredByMaster || !workflows.length}
            onChange={(event) => onEnabledChange(event.target.checked)}
            inputProps={{ 'aria-label': `Override ${label}` }}
          />
        </Stack>
      }
    />
  );
};

/**
 * Keeps a workflow that is configured but no longer selectable visible on the trigger.
 *
 * Without the tombstone the select silently falls back to its placeholder while the config
 * still names that workflow — a switch reading "on" above an apparently empty selection,
 * which is the last thing anyone should have to decode mid-incident. It cannot be re-picked.
 */
const WorkflowEmergencyOverrideContent = () => {
  const {
    config,
    loading,
    isSaving,
    setMasterEnabled,
    setTriggerEnabled,
    setTriggerWorkflow,
  } = useWorkflowEmergencyOverride();
  const { workflowsByTrigger, loading: workflowsLoading } = usePlatformWorkflowsByTrigger();

  if (loading) return null;

  const masterEnabled = config.master === true;

  return (
    <SettingsSection
      title="Emergency workflow override"
      description="Break-glass incident control. While a trigger is overridden, it runs the selected platform workflow for every partner instead of the partner's own."
    >
      <SettingsSectionRow
        title={MASTER_LABEL}
        secondary="The kill switch: turns every trigger's override on at once, so a partner-wide incident is one click. Triggers with a workflow selected below run that workflow; the rest fall back to whichever platform workflow matches. Turn it back off once the incident is over."
        control={
          <Switch
            checked={masterEnabled}
            disabled={isSaving}
            onChange={(event) => void setMasterEnabled(event.target.checked)}
            inputProps={{ 'aria-label': MASTER_LABEL }}
          />
        }
      />

      {ORDERED_WORKFLOW_TRIGGER_TYPES.map((triggerType) => (
        <TriggerOverrideRow
          key={triggerType}
          triggerType={triggerType}
          workflowId={config.triggers?.[triggerType]?.workflowId}
          enabled={masterEnabled || config.triggers?.[triggerType]?.enabled === true}
          coveredByMaster={masterEnabled}
          workflows={workflowsByTrigger[triggerType] ?? []}
          workflowsLoading={workflowsLoading}
          disabled={isSaving || workflowsLoading}
          onEnabledChange={(enabled) => void setTriggerEnabled(triggerType, enabled)}
          onWorkflowChange={(selectedId) => void setTriggerWorkflow(triggerType, selectedId)}
        />
      ))}
    </SettingsSection>
  );
};

function buildOptions(
  workflows: PlatformWorkflowOption[],
  workflowId: string | undefined,
): SelectMenuOption[] {
  const options: SelectMenuOption[] = workflows.map((workflow) => ({
    value: workflow.workflowId,
    title: workflow.name,
  }));

  if (workflowId && !workflows.some((workflow) => workflow.workflowId === workflowId)) {
    options.push({
      value: workflowId,
      title: 'Selected workflow is no longer active',
      disabled: true,
    });
  }

  return options;
}

function triggerSecondary({
  label,
  coveredByMaster,
  workflowsLoading,
  hasWorkflows,
}: {
  label: string;
  coveredByMaster: boolean;
  workflowsLoading: boolean;
  hasWorkflows: boolean;
}): string {
  if (workflowsLoading) {
    return 'Loading platform workflows…';
  }

  if (!hasWorkflows) {
    return `No platform workflow is active for ${label.toLowerCase()}, so there is nothing to force yet.`;
  }

  if (coveredByMaster) {
    return 'Held on by the master switch above. Turn that off to control this trigger on its own.';
  }

  return 'Forces every partner onto the selected workflow for this trigger, over their own. Only new executions are affected — anything already in flight finishes on the workflow it started with.';
}

/**
 * Settings → Preferences control for `workflow_emergency_override`.
 *
 * Rendered only with the platform partner selected, and only for users holding
 * `access_to_platform_partner`. Both conditions are the honest ones: the backend reads the
 * setting from the `partner_id = 0` row alone, so a row written against any other partner
 * would be silently ignored, and a toggle that looks live but does nothing is worse than no
 * toggle. Gating here also keeps the platform workflow query off every other partner's
 * settings page.
 */
export const WorkflowEmergencyOverridePreferences = () => {
  const { groupRules } = useGroupRules();
  const partnerId = useGlobalStore.use.selectedPartnerId();

  if (partnerId !== PLATFORM_PARTNER_ID || !hasPlatformPartnerAccess(groupRules)) {
    return null;
  }

  return <WorkflowEmergencyOverrideContent />;
};
